class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class CartDrawerShipping {
  constructor() {
    this.updateFromServerCart();
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      // Prefer cartData on the event payload to avoid a second network roundtrip.
      if (event && event.cartData && typeof event.cartData.total_price === 'number') {
        this.update(event.cartData.total_price);
      } else {
        this.updateFromServerCart();
      }
    });
  }

  updateFromServerCart() {
    fetch(window.Shopify.routes.root + 'cart.js')
      .then((r) => r.json())
      .then((cart) => this.update(cart.total_price || 0))
      .catch(() => {});
  }

  update(totalPrice) {
    const container = document.getElementById('CartDrawer-Shipping');
    if (!container) return;

    const standardThreshold = parseInt(container.dataset.standardThreshold, 10);
    const priorityThreshold = parseInt(container.dataset.priorityThreshold, 10);
    const standardLabel = container.dataset.standardLabel || 'free standard shipping';
    const priorityLabel = container.dataset.priorityLabel || 'free next day shipping';
    const moneyFormat = container.dataset.moneyFormat || '${{amount}}';
    const symbolMatch = moneyFormat.match(/^([^\d{]*)/);
    const currencySymbol = (symbolMatch && symbolMatch[1]) || '£';
    const bar = document.getElementById('CartDrawer-ShippingBar');
    const message = document.getElementById('CartDrawer-ShippingMessage');

    if (!bar || !message) return;

    const trackColor = bar.dataset.track || '#E5E0D6';
    const standardColor = bar.dataset.standardColor || '#3C2415';
    const priorityColor = bar.dataset.priorityColor || '#8B6F47';

    const total = totalPrice || 0;
    const fmt = (cents) => currencySymbol + Math.ceil(cents / 100);
    const tier = (label) => `<em class="free-shipping__tier">${label}</em>`;

    let gradient;
    if (total >= priorityThreshold) {
      gradient = `linear-gradient(to right, ${standardColor} 0 50%, ${priorityColor} 50% 100%)`;
    } else if (total >= standardThreshold) {
      const p = (total - standardThreshold) / (priorityThreshold - standardThreshold);
      const stop = (50 + p * 50).toFixed(2);
      gradient = `linear-gradient(to right, ${standardColor} 0 50%, ${priorityColor} 50% ${stop}%, ${trackColor} ${stop}% 100%)`;
    } else if (standardThreshold > 0) {
      const stop = ((total / standardThreshold) * 50).toFixed(2);
      gradient = `linear-gradient(to right, ${standardColor} 0 ${stop}%, ${trackColor} ${stop}% 100%)`;
    } else {
      gradient = trackColor;
    }
    bar.style.background = gradient;

    if (total === 0) {
      message.innerHTML = `Spend ${fmt(standardThreshold)} to unlock ${tier(standardLabel)}`;
    } else if (total >= priorityThreshold) {
      message.innerHTML = `You've unlocked ${tier(priorityLabel)}`;
    } else if (total >= standardThreshold) {
      message.innerHTML = `${fmt(priorityThreshold - total)} away from ${tier(priorityLabel)}`;
    } else {
      message.innerHTML = `${fmt(standardThreshold - total)} away from ${tier(standardLabel)}`;
    }
  }

  // Optimistic bump so the bar moves the instant a user clicks +/- or remove,
  // without waiting for the cart-update fetch to return.
  bumpOptimistic(deltaCents) {
    const container = document.getElementById('CartDrawer-Shipping');
    if (!container) return;
    const current = this._lastTotal || 0;
    const next = Math.max(0, current + deltaCents);
    this._lastTotal = next;
    this.update(next);
  }
}

// Patch renderContents so the bar updates the moment new cart HTML is injected,
// reading total_price directly from the parsed state instead of waiting on a fetch.
const _renderContents = CartDrawer.prototype.renderContents;
CartDrawer.prototype.renderContents = function (parsedState) {
  _renderContents.call(this, parsedState);
  if (window._cartDrawerShipping) {
    if (parsedState && typeof parsedState.total_price === 'number') {
      window._cartDrawerShipping._lastTotal = parsedState.total_price;
      window._cartDrawerShipping.update(parsedState.total_price);
    } else {
      window._cartDrawerShipping.updateFromServerCart();
    }
  }
};

document.addEventListener('DOMContentLoaded', () => {
  if (document.getElementById('CartDrawer-Shipping')) {
    window._cartDrawerShipping = new CartDrawerShipping();
  }
});
